import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useMutation, useQuery } from "@tanstack/react-query";
import { VariableSizeList } from "react-window";

import { SearchTableInput } from "../SearchTableInput/SearchTableInput";
import { ModalConfirmWindow } from "./components/ModalConfirmWindow/ModalConfirmWindow";
import { AddUserModal } from "./components/AddUserModal/AddUserModal";
import { AddSuperuserModal } from "./components/AddSuperuserModal/AddSuperuserModal";

import { FaPlus, FaSpinner, FaSortAlphaDown, FaSortAlphaUp } from "react-icons/fa";
import { BiSave } from "react-icons/bi";
import { AiOutlineEdit, AiOutlineClose } from "react-icons/ai";
import { DeleteButton } from "../../../../components/Admin";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { selectStyles } from "./types/SelectStyles";
import { IUserRole } from "./interfaces/UserRole";
import { INewRole } from "./components/AddUserModal/interfaces/role.interface";
import { ITeam, ITeamList } from "../../interfaces/team.interface";

import { fetchTeamData } from "./services/fetchTeamData.service";
import { addNewUser } from "./services/addNewUser.service";
import { addNewSuperuser } from "./services/addNewSuperuser.service";
import { updateUserRole } from "./services/updateUserRole.service";
import { deleteUserRole } from "./services/deleteUserRole.service";

import { getRightId } from "../../helpers/rightIdOptions";
import { useUserContext } from "../../../../context/UserContext";
import { canUserEdit } from "../../helpers/userRights";

interface IManageUserTabProps {
  darkMode: boolean;
  teams: { value: string; label: string }[];
  userRoleList: IUserRole[];
  isSelectedTeam: boolean;
  selectedTeam: ITeam | null;
  teamId: any;
  teamList: ITeamList[];
  allUserList: any[];
  userList: { value: string; label: string }[];
  roleOptions: { value: string; label: string }[];
  activeTab: string;
}

type RowKey = string | number;
type ResponsiveMode = "desktop" | "compact";
type UpdateChangeType = "role" | "team" | "both" | "none";

const HEADER_H = 44;
const FALLBACK_ROW_H = 64;
const LOADER_ROW_H = 56;
const COMPACT_BREAKPOINT = 640;

const addPx = (value: unknown, delta: number) => {
  if (typeof value === "number") return value + delta;
  if (typeof value === "string" && value.endsWith("px")) {
    return `${parseFloat(value) + delta}px`;
  }
  return value;
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const cn = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(" ");

const ManageUserTab: React.FC<IManageUserTabProps> = ({
  darkMode,
  teams,
  userRoleList,
  isSelectedTeam,
  selectedTeam,
  teamId,
  teamList,
  allUserList,
  userList,
  roleOptions,
  activeTab,
}) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const effectiveUserForHighlight = user ?? null;

  const [editRoleId, setEditRoleId] = useState<RowKey | null>(null);
  const [editTeamId, setEditTeamId] = useState<RowKey | null>(null);

  const [searchValue, setSearchValue] = useState<string>("");
  const [userRoles, setUserRoles] = useState(userRoleList);

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState<boolean>(true);

  const [roleFilter] = useState<{ value: string; label: string } | null>(null);
  const [teamFilter] = useState<{ value: string; label: string } | null>(null);

  const [roleFiltersSelected] = useState<string[]>([]);

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showAddSuperuserModal, setShowAddSuperuserModal] = useState(false);

  const [showRemoveUserRoleModal, setShowRemoveUserRoleModal] = useState<{
    visible: boolean;
    id: number;
  }>({ visible: false, id: 0 });

  const backupRolesRef = useRef<Record<string, string>>({});
  const backupTeamsRef = useRef<Record<string, { id: number | null; name: string }>>({});

  const listOuterRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<VariableSizeList | null>(null);

  const rowHeights = useRef<Map<RowKey, number>>(new Map());

  const listAreaRef = useRef<HTMLDivElement | null>(null);
  const [listHeight, setListHeight] = useState<number>(420);
  const [listAreaWidth, setListAreaWidth] = useState<number>(0);

  const teamOptions = useMemo(
    () => [
      { value: "", label: "-" },
      ...(teamList ?? []).map((team) => ({
        value: String(team.id),
        label: team.name,
      })),
    ],
    [teamList],
  );

  const canEdit = canUserEdit(user, selectedTeam);
  const TOASTER_ID = "add-user-toaster";

  const resolveTeamObject = useCallback(
    (teamValue: any) => {
      if (
        teamValue &&
        typeof teamValue === "object" &&
        "id" in teamValue &&
        "name" in teamValue
      ) {
        return {
          id: Number((teamValue as any).id),
          name: String((teamValue as any).name),
        };
      }

      if (teamValue === null || teamValue === undefined || teamValue === "") return null;

      const matched = (teamList ?? []).find((team) => String(team.id) === String(teamValue));
      if (!matched) return null;

      return {
        id: matched.id,
        name: matched.name,
      };
    },
    [teamList],
  );

  const resolveRoleLabel = useCallback(
    (rightId: string | number | null | undefined, fallbackRight?: string) => {
      if (fallbackRight) return fallbackRight;
      return (
        roleOptions.find((opt) => String(opt.value) === String(rightId))?.label ?? "Viewer"
      );
    },
    [roleOptions],
  );

  useEffect(() => {
    const update = () => {
      requestAnimationFrame(() => {
        const el = listAreaRef.current;
        if (!el) return;

        const h = el.clientHeight;
        const w = el.clientWidth;

        if (h > 0) setListHeight(h);
        if (w > 0) setListAreaWidth(w);
      });
    };

    update();

    const RO = (window as any).ResizeObserver;
    let ro: any = null;

    if (RO && listAreaRef.current) {
      ro = new RO(() => update());
      ro.observe(listAreaRef.current);
    }

    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ["teamData", teamId],
    queryFn: () => fetchTeamData(teamId),
    enabled: !!teamId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { mutate: mutateAddNewUser } = useMutation({
    mutationFn: async (payload: {
      items: {
        name: string | null;
        email: string;
        right_id: number | string;
        team_id: number | null;
      }[];
      isMulti?: boolean;
    }) => addNewUser(payload.items),
    onSuccess: (res: any, variables) => {
      const created = Array.isArray(res) ? res : [res];
      const submittedItems = variables?.items ?? [];

      const adapted = created.map((r: any, index: number) => {
        const submitted = submittedItems[index];

        const normalizedTeam =
          selectedTeam
            ? { id: selectedTeam.id, name: selectedTeam.name }
            : resolveTeamObject(submitted?.team_id) ??
              resolveTeamObject(r?.team);

        const normalizedRightId = Number(
          r?.right_id ??
            submitted?.right_id ??
            getRightId(r?.right ?? "Viewer"),
        );

        const normalizedRight = resolveRoleLabel(normalizedRightId, r?.right);

        return {
          ...r,
          team: normalizedTeam,
          right_id: normalizedRightId,
          right: normalizedRight,
        };
      });

      setUserRoles((prev: any) => [...prev, ...adapted]);

      const isBulk = variables?.isMulti || (variables?.items?.length ?? 0) > 1;
      if (isBulk) {
        if (!toast.isActive("group-add-toaster")) toast.success(t("adminPanel.addUserListToaster"));
      } else {
        toast.success(t("adminPanel.addUserToaster"), { toastId: TOASTER_ID });
      }
    },
    onError: (error: Error) => {
      console.error("Error adding Users:", error);
      toast.error("Error adding User Role");
    },
  });

  const { mutate: mutateAddNewSuperuser } = useMutation({
    mutationFn: async (newUserData: { name: string | null; email: string }) => {
      const matchedUser = allUserList.find(
        (u: any) => u.email.toLowerCase() === newUserData.email.toLowerCase(),
      );
      return addNewSuperuser(newUserData.name, newUserData.email, matchedUser?.id);
    },
    onSuccess: (res: any) => {
      if (res && res.is_superuser) {
        setUserRoles((prevUsers: any) => [...prevUsers, res]);
        toast.success(t("adminPanel.addUserToaster"), { toastId: TOASTER_ID });
      }
    },
  });

  const { mutate: updateRole } = useMutation({
    mutationFn: async (payload: {
      id: number | string;
      name: string;
      email: string;
      team: number | null;
      right_id: number;
      changeType: UpdateChangeType;
    }) =>
      updateUserRole(
        payload.id,
        payload.name.split("<")[0].trim(),
        payload.email,
        payload.team ?? null,
        payload.right_id,
      ),

    onSuccess: (_res, variables) => {
      const toastKey =
        variables.changeType === "team" || variables.changeType === "both"
          ? "adminPanel.teamChangeToaster"
          : "adminPanel.roleChangeToaster";

      toast.success(t(toastKey));
    },

    onError: (error: Error) => console.error("Error updating user role:", error),
  });

  const { mutate: mutateDeletedUserRole } = useMutation({
    mutationFn: async (id: number) => deleteUserRole(id),
    onSuccess: () => {
      setUserRoles((prev: any[]) => {
        let updatedUsers = [...prev];
        const targetIndex = updatedUsers.findIndex((u) => u.id === showRemoveUserRoleModal.id);

        if (targetIndex !== -1) {
          updatedUsers[targetIndex].team = null;
          updatedUsers[targetIndex].right = "Viewer";
          updatedUsers[targetIndex].right_id = getRightId("Viewer");
        }

        if (isSelectedTeam) {
          updatedUsers = updatedUsers.filter((u: any) => u.id !== showRemoveUserRoleModal.id);
        }

        return updatedUsers;
      });

      toast.success(t("adminPanel.roleChangeToaster"));
    },
    onError: () => toast.error("Error deleting User Role"),
  });

  useEffect(() => {
    if (data && Array.isArray((data as any).roles)) {
      const adaptedRoles = (data as any).roles.map((role: any) => ({
        ...role,
        right_id: getRightId(role.right),
        team: { id: (data as any).id, name: (data as any).name },
      }));
      setUserRoles(adaptedRoles);
    }
  }, [data]);

  useEffect(() => {
    if (!teamId) setUserRoles(userRoleList);
  }, [userRoleList, teamId]);

  const getSearchValue = (v: string) => setSearchValue(v.toLowerCase());

  const highlightText = useCallback(
    (text: string) => {
      if (!searchValue) return text;
      const safeSearch = escapeRegExp(searchValue);
      const regex = new RegExp(`(${safeSearch})`, "gi");
      return text.replace(
        regex,
        (match) => `<span class='bg-yellow-300 text-black font-bold'>${match}</span>`,
      );
    },
    [searchValue],
  );

  const filteredUsers = useMemo(() => {
    let out = userRoles;

    if (searchValue) {
      const s = searchValue;
      out = out.filter(
        (u: any) =>
          u.user.name.toLowerCase().includes(s) ||
          u.user.email.toLowerCase().includes(s) ||
          u.right.toLowerCase().includes(s) ||
          u.team?.name?.toLowerCase().includes(s),
      );
    }

    if (roleFilter?.value && roleFilter.value !== "all") {
      out = out.filter((u: any) => String(u.right_id) === String(roleFilter.value));
    }

    if (teamFilter?.value && teamFilter.value !== "all") {
      out = out.filter((u: any) => String(u.team?.name ?? "-") === String(teamFilter.value));
    }

    if (roleFiltersSelected.length > 0) {
      out = out.filter((u: any) => roleFiltersSelected.includes(String(u.right_id)));
    }

    if (sortKey) {
      out = [...out].sort((a: any, b: any) => {
        let va: any = "";
        let vb: any = "";
        if (sortKey === "name") {
          va = (a.user?.name || "").toLowerCase();
          vb = (b.user?.name || "").toLowerCase();
        } else if (sortKey === "email") {
          va = (a.user?.email || "").toLowerCase();
          vb = (b.user?.email || "").toLowerCase();
        } else if (sortKey === "role") {
          va = (a.right || "").toLowerCase();
          vb = (b.right || "").toLowerCase();
        } else if (sortKey === "team") {
          va = (a.team?.name || "").toLowerCase();
          vb = (b.team?.name || "").toLowerCase();
        }
        if (va < vb) return sortAsc ? -1 : 1;
        if (va > vb) return sortAsc ? 1 : -1;
        return 0;
      });
    }

    const userIdentifier = (() => {
      const u = effectiveUserForHighlight;
      if (u?.email) return { type: "email", value: String(u.email).toLowerCase() };
      if (u?.id !== undefined && u?.id !== null) return { type: "id", value: String(u.id) };
      if (u?.name) return { type: "name", value: String(u.name).toLowerCase() };
      return null;
    })();

    if (userIdentifier) {
      if (!isSelectedTeam && userIdentifier.type === "email") {
        const matches = out.filter(
          (u: any) => String(u.user?.email || "").toLowerCase() === userIdentifier.value,
        );
        if (matches.length) {
          out = [
            ...matches,
            ...out.filter(
              (u: any) => String(u.user?.email || "").toLowerCase() !== userIdentifier.value,
            ),
          ];
        }
      } else {
        const meFull = out.find((u: any) => {
          const uu = u.user || {};
          if (userIdentifier.type === "email")
            return String(uu.email || "").toLowerCase() === userIdentifier.value;
          if (userIdentifier.type === "id") return String(uu.id ?? "") === userIdentifier.value;
          return String(uu.name || "").toLowerCase() === userIdentifier.value;
        });
        if (meFull) out = [meFull, ...out.filter((u: any) => u.id !== meFull.id)];
      }
    }

    return out;
  }, [
    userRoles,
    searchValue,
    roleFilter,
    teamFilter,
    sortKey,
    sortAsc,
    roleFiltersSelected,
    user,
    effectiveUserForHighlight,
    isSelectedTeam,
  ]);

  const responsiveMode = useMemo<ResponsiveMode>(() => {
    if (listAreaWidth > 0 && listAreaWidth < COMPACT_BREAKPOINT) return "compact";
    return "desktop";
  }, [listAreaWidth]);

  const hasRoleColumn = responsiveMode === "desktop";
  const hasTeamColumn = responsiveMode === "desktop";
  const hasActionsColumn = responsiveMode === "desktop";

  const showInlineMeta = responsiveMode === "compact";
  const showActionsInline = responsiveMode === "compact";

  const tableMinWidth = useMemo(() => {
    if (responsiveMode === "compact") return 260 + 320;
    return 240 + 320 + 160 + 220 + 140;
  }, [responsiveMode]);

  const GRID_STYLE = useMemo<React.CSSProperties>(() => {
    if (responsiveMode === "compact") {
      return {
        gridTemplateColumns: "minmax(260px,320px) minmax(320px,1fr)",
      };
    }

    return {
      gridTemplateColumns: "minmax(240px,1fr) 320px 160px 220px 140px",
    };
  }, [responsiveMode]);

  const TABLE_GRID_STYLE = useMemo<React.CSSProperties>(
    () => ({
      ...GRID_STYLE,
      width: "100%",
      minWidth: `${tableMinWidth}px`,
    }),
    [GRID_STYLE, tableMinWidth],
  );

  const commonSelectStyles = useMemo(
    () => ({
      ...selectStyles(darkMode),
      control: (base: any) => ({ ...base, width: "100%" }),
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
      option: (base: any, { isFocused }: any) => ({
        ...base,
        backgroundColor: isFocused ? "#86BC25" : darkMode ? "#374151" : "#FFFFFF",
        color: darkMode ? "#FFFFFF" : "#000000",
      }),
    }),
    [darkMode],
  );

  const showLoaderRow = !!filteredUsers.length && isFetching && !isLoading;
  const dataCount = filteredUsers.length;
  const itemCount = dataCount + (showLoaderRow ? 1 : 0);

  const getItemKey = useCallback(
    (index: number): RowKey => {
      if (showLoaderRow && index === dataCount) return "__loader__";
      return filteredUsers[index]?.id ?? index;
    },
    [filteredUsers, showLoaderRow, dataCount],
  );

  const getRowSize = useCallback(
    (index: number) => {
      if (showLoaderRow && index === dataCount) return LOADER_ROW_H;
      return rowHeights.current.get(getItemKey(index)) ?? FALLBACK_ROW_H;
    },
    [getItemKey, showLoaderRow, dataCount],
  );

  const setRowSizeForIndex = useCallback(
    (index: number, size: number) => {
      if (!Number.isFinite(size) || size < 32) return;
      const key = getItemKey(index);
      const prev = rowHeights.current.get(key);
      if (prev !== size) {
        rowHeights.current.set(key, size);
        listRef.current?.resetAfterIndex(index, true);
      }
    },
    [getItemKey],
  );

  useEffect(() => {
    listRef.current?.resetAfterIndex(0, true);
  }, [
    filteredUsers.length,
    searchValue,
    selectedTeam?.id,
    teamId,
    roleFilter,
    teamFilter,
    showLoaderRow,
    responsiveMode,
  ]);

  useEffect(() => {
    setEditRoleId(null);
    setEditTeamId(null);
    backupRolesRef.current = {};
    backupTeamsRef.current = {};
    rowHeights.current = new Map();

    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = listAreaRef.current;
        if (el) {
          if (el.clientHeight > 0) setListHeight(el.clientHeight);
          if (el.clientWidth > 0) setListAreaWidth(el.clientWidth);
        }

        listRef.current?.resetAfterIndex(0, true);
        listRef.current?.scrollTo(0);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [activeTab, selectedTeam, teamId]);

  useEffect(() => {
    listRef.current?.scrollTo(0);
  }, [searchValue, selectedTeam?.id, teamId, roleFilter, teamFilter, responsiveMode]);

  const patchUserRoleById = (id: RowKey, patch: any) => {
    setUserRoles((prev: any[]) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const findUserRoleById = (id: RowKey) => userRoles.find((u: any) => u.id === id);

  const handleRoleChange = (selectedOption: any, id: RowKey) => {
    patchUserRoleById(id, { right_id: selectedOption.value, right: selectedOption.label });
  };

  const handleTeamChange = (selectedOption: any, id: RowKey) => {
    if (!selectedOption?.value) {
      patchUserRoleById(id, {
        team: null,
        right: "Viewer",
        right_id: getRightId("Viewer"),
      });
      return;
    }

    patchUserRoleById(id, {
      team: { id: Number(selectedOption.value), name: selectedOption.label },
    });
  };

  const startEdit = (id: RowKey) => {
    const key = String(id);
    const current = findUserRoleById(id);
    if (!current) return;

    if (editRoleId !== null && editRoleId !== id) {
      const prevKey = String(editRoleId);
      const prevBefore = backupRolesRef.current[prevKey];
      if (prevBefore !== undefined) {
        patchUserRoleById(editRoleId, { right: prevBefore, right_id: getRightId(prevBefore) });
        delete backupRolesRef.current[prevKey];
      }
    }

    if (editTeamId !== null && editTeamId !== id) {
      const prevKey = String(editTeamId);
      const prevTeam = backupTeamsRef.current[prevKey];
      if (prevTeam !== undefined) {
        patchUserRoleById(editTeamId, {
          team: prevTeam.id ? { id: prevTeam.id, name: prevTeam.name } : null,
        });
        delete backupTeamsRef.current[prevKey];
      }
    }

    backupRolesRef.current[key] = current.right;
    backupTeamsRef.current[key] = {
      id: current.team?.id ?? null,
      name: current.team?.name ?? "-",
    };

    setEditRoleId(id);
    setEditTeamId(id);
  };

  const saveEdit = (id: RowKey) => {
    const key = String(id);
    const current = findUserRoleById(id);
    if (!current) return;

    const beforeRole = backupRolesRef.current[key];
    const beforeTeam = backupTeamsRef.current[key];

    const currentTeamId = current.team?.id ?? null;
    const teamToSend = currentTeamId != null ? Number(currentTeamId) : null;
    const rightIdToSend = teamToSend ? getRightId(current.right) : getRightId("Viewer");

    if (!teamToSend) {
      patchUserRoleById(id, {
        right: "Viewer",
        right_id: getRightId("Viewer"),
      });
    }

    const previousTeamId = beforeTeam?.id ?? null;
    const previousRightId =
      previousTeamId != null ? getRightId(beforeRole ?? "Viewer") : getRightId("Viewer");

    const teamChanged = String(previousTeamId ?? "") !== String(teamToSend ?? "");
    const roleChanged = String(previousRightId) !== String(rightIdToSend);

    let changeType: UpdateChangeType = "none";
    if (teamChanged && roleChanged) changeType = "both";
    else if (teamChanged) changeType = "team";
    else if (roleChanged) changeType = "role";

    if (changeType === "none") {
      delete backupRolesRef.current[key];
      delete backupTeamsRef.current[key];
      setEditRoleId(null);
      setEditTeamId(null);
      return;
    }

    updateRole({
      id: current.id?.toString() ?? "",
      name: current.user.name.split("<")[0].trim(),
      email: current.user.email,
      team: teamToSend,
      right_id: rightIdToSend,
      changeType,
    });

    delete backupRolesRef.current[key];
    delete backupTeamsRef.current[key];
    setEditRoleId(null);
    setEditTeamId(null);
  };

  const cancelEdit = (id: RowKey) => {
    const key = String(id);
    const beforeRole = backupRolesRef.current[key];
    if (beforeRole !== undefined) {
      patchUserRoleById(id, { right: beforeRole, right_id: getRightId(beforeRole) });
      delete backupRolesRef.current[key];
    }

    const beforeTeam = backupTeamsRef.current[key];
    if (beforeTeam !== undefined) {
      patchUserRoleById(id, {
        team: beforeTeam.id ? { id: beforeTeam.id, name: beforeTeam.name } : null,
      });
      delete backupTeamsRef.current[key];
    }

    setEditRoleId(null);
    setEditTeamId(null);
  };

  const handleAddUser = (newUsers: INewRole[], isMulti: boolean) => {
    const items = newUsers.map((u) => ({
      name: u.name ? u.name.split("<")[0].trim() : "",
      email: u.email,
      right_id: Number(u.right_id),
      team_id: Number(u.team_id) === 0 ? null : Number(u.team_id),
    }));
    mutateAddNewUser({ items, isMulti });
  };

  const handleAddSuperuser = (newUser: INewRole) => {
    mutateAddNewSuperuser({
      name: newUser.name ? newUser.name.split("<")[0].trim() : "",
      email: newUser.email,
    });
  };

  const handleRemoveUserRole = () => {
    mutateDeletedUserRole(showRemoveUserRoleModal.id);
    setShowRemoveUserRoleModal((p) => ({ ...p, visible: false }));
  };

  const getInitials = (name?: string) => {
    if (!name) return "—";
    const parts = name.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase()).join("") || "—";
  };

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortAsc((s) => !s);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const getRoleChipClassName = (role?: string) => {
    const r = (role || "").toLowerCase();
    if (r.includes("super"))
      return "inline-flex items-center rounded-lg border border-del-green bg-del-light-theme-green/20 text-del-light-theme-green px-2 py-0.5 text-xs font-medium";
    if (r.includes("admin"))
      return "inline-flex items-center rounded-lg border border-del-green bg-del-light-theme-green/20 text-del-light-theme-green px-2 py-0.5 text-xs font-medium";
    if (r.includes("editor") || r.includes("manager"))
      return "inline-flex items-center rounded-lg border border-sky-300 bg-sky-200 text-sky-900 px-2 py-0.5 text-xs font-medium";
    if (r.includes("viewer"))
      return "inline-flex items-center rounded-lg border border-slate-300 bg-slate-200 text-slate-900 dark:bg-slate-800/50 dark:text-slate-200 px-2 py-0.5 text-xs font-medium";
    return "inline-flex items-center rounded-lg border border-slate-300 bg-slate-200 dark:bg-slate-900/40 text-slate-700 dark:text-slate-300 px-2 py-0.5 text-xs font-medium";
  };

  const HeaderRow = useCallback(
    () => (
      <div
        className={[
          "grid items-center h-11 sticky top-0 z-20",
          "bg-white dark:bg-slate-900",
          "text-xs font-semibold uppercase tracking-wide",
          "text-slate-600 dark:text-slate-300",
          "border-b border-slate-200 dark:border-slate-700",
          "divide-x divide-slate-200 dark:divide-slate-700",
        ].join(" ")}
        style={{
          ...TABLE_GRID_STYLE,
          boxSizing: "border-box",
        }}
      >
        <div className="px-4 py-3 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate whitespace-nowrap">{t("adminPanel.columns.name")}</div>
            <button
              type="button"
              onClick={() => toggleSort("name")}
              className="h-6 w-6 inline-flex items-center justify-center rounded transition hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Sort by name"
            >
              {sortKey === "name" ? (
                sortAsc ? (
                  <FaSortAlphaDown />
                ) : (
                  <FaSortAlphaUp />
                )
              ) : (
                <FaSortAlphaDown className="opacity-40" />
              )}
            </button>
          </div>
        </div>

        <div className="px-4 py-3 min-w-0 overflow-hidden">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate whitespace-nowrap">{t("adminPanel.columns.email")}</div>
            <button
              type="button"
              onClick={() => toggleSort("email")}
              className="h-6 w-6 inline-flex items-center justify-center rounded transition hover:bg-slate-100 dark:hover:bg-slate-800"
              aria-label="Sort by email"
            >
              {sortKey === "email" ? (
                sortAsc ? (
                  <FaSortAlphaDown />
                ) : (
                  <FaSortAlphaUp />
                )
              ) : (
                <FaSortAlphaDown className="opacity-40" />
              )}
            </button>
          </div>
        </div>

        {hasRoleColumn && (
          <div className="px-4 py-3 min-w-0 overflow-hidden relative">
            <div className="flex items-center justify-between gap-2">
              <div className="truncate whitespace-nowrap">{t("adminPanel.columns.role")}</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleSort("role")}
                  className="h-6 w-6 inline-flex items-center justify-center rounded transition hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label="Sort by role"
                >
                  {sortKey === "role" ? (
                    sortAsc ? (
                      <FaSortAlphaDown />
                    ) : (
                      <FaSortAlphaUp />
                    )
                  ) : (
                    <FaSortAlphaDown className="opacity-40" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {hasTeamColumn && (
          <div className="px-4 py-3 min-w-0 overflow-hidden">
            <div className="flex items-center justify-between gap-2">
              <div className="truncate whitespace-nowrap">{t("adminPanel.columns.team")}</div>
              <button
                type="button"
                onClick={() => toggleSort("team")}
                className="h-6 w-6 inline-flex items-center justify-center rounded transition hover:bg-slate-100 dark:hover:bg-slate-800"
                aria-label="Sort by team"
              >
                {sortKey === "team" ? (
                  sortAsc ? (
                    <FaSortAlphaDown />
                  ) : (
                    <FaSortAlphaUp />
                  )
                ) : (
                  <FaSortAlphaDown className="opacity-40" />
                )}
              </button>
            </div>
          </div>
        )}

        {hasActionsColumn && (
          <div className="px-4 py-3 text-center min-w-0 overflow-hidden truncate whitespace-nowrap">
            {t("adminPanel.columns.actions", { defaultValue: "Actions" })}
          </div>
        )}
      </div>
    ),
    [t, TABLE_GRID_STYLE, hasRoleColumn, hasTeamColumn, hasActionsColumn, sortKey, sortAsc],
  );

  const OuterElement = useMemo(() => {
    const C = React.forwardRef<HTMLDivElement, any>(({ style, className, ...rest }, ref) => (
      <div
        ref={ref}
        {...rest}
        className={["relative", className].filter(Boolean).join(" ")}
        style={{
          ...style,
          overflow: "auto",
          ...({ overscrollBehavior: "contain" } as any),
          ...({ scrollbarGutter: "stable both-edges" } as any),
        }}
      />
    ));
    C.displayName = "ManageUsersOuterElement";
    return C;
  }, []);

  const InnerElement = useMemo(() => {
    const C = React.forwardRef<HTMLDivElement, any>(({ style, children, ...rest }, ref) => {
      const rowsHeight = style?.height;

      return (
        <div
          ref={ref}
          {...rest}
          style={{
            ...style,
            height: addPx(rowsHeight, HEADER_H),
            minWidth: `${tableMinWidth}px`,
          }}
        >
          <HeaderRow />
          <div
            style={{
              position: "relative",
              height: rowsHeight,
              minWidth: `${tableMinWidth}px`,
            }}
          >
            {children}
          </div>
        </div>
      );
    });

    C.displayName = "ManageUsersInnerElement";
    return C;
  }, [HeaderRow, tableMinWidth]);

  const Row = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const isLoaderRow = showLoaderRow && index === dataCount;
    const userRole = isLoaderRow ? null : filteredUsers[index];

    const rowRef = useRef<HTMLDivElement>(null);
    const idKey: RowKey = ((userRole as any)?.id ?? index) as RowKey;

    const isSelf = (() => {
      if (!effectiveUserForHighlight || !userRole) return false;
      try {
        if (effectiveUserForHighlight.email && userRole.user?.email)
          return (
            String(userRole.user.email).toLowerCase() ===
            String(effectiveUserForHighlight.email).toLowerCase()
          );
        if (effectiveUserForHighlight.id != null && userRole.user?.id != null)
          return String(userRole.user.id) === String(effectiveUserForHighlight.id);
        if (effectiveUserForHighlight.name && userRole.user?.name)
          return (
            String(userRole.user.name).toLowerCase() ===
            String(effectiveUserForHighlight.name).toLowerCase()
          );
      } catch (e) {
        // fallthrough
      }
      return false;
    })();

    const rowCanEdit =
      !!userRole &&
      canEdit &&
      userRole.user?.email?.toLowerCase() !== user?.email?.toLowerCase() &&
      !["Superadmin", "Superuser"].includes(userRole.right);

    const rowHasTeam = !!userRole?.team;
    const rowCanManage = rowCanEdit && rowHasTeam;
    const rowCanDelete = rowCanEdit && rowHasTeam;
    const isEditing = editRoleId === idKey || editTeamId === idKey;

    useLayoutEffect(() => {
      if (!userRole || isLoaderRow) return;

      const measure = () => {
        if (!rowRef.current) return;
        const h = rowRef.current.getBoundingClientRect().height;
        setRowSizeForIndex(index, h);
      };

      requestAnimationFrame(measure);

      const RO = (window as any).ResizeObserver;
      if (!RO || !rowRef.current) return;

      const ro = new RO(() => measure());
      ro.observe(rowRef.current);

      return () => ro.disconnect();
    }, [
      index,
      userRole,
      isLoaderRow,
      editRoleId,
      editTeamId,
      searchValue,
      responsiveMode,
      setRowSizeForIndex,
    ]);

    if (isLoaderRow) {
      return (
        <div style={style} className="flex items-center justify-center">
          <FaSpinner className="animate-spin" size={18} color={darkMode ? "#FFFFFF" : "#000000"} />
        </div>
      );
    }

    if (!userRole) return <div style={style} />;

    const roleField =
      editRoleId === idKey ? (
        <Select
          value={roleOptions.find((opt: any) => Number(opt.value) === Number(userRole.right_id))}
          isSearchable={false}
          isDisabled={!userRole.team}
          options={roleOptions}
          onChange={(opt) => handleRoleChange(opt, idKey)}
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          menuPlacement="auto"
          menuPosition="fixed"
          styles={commonSelectStyles as any}
        />
      ) : (
        <span
          className={`${getRoleChipClassName(userRole.right)} max-w-full min-w-0 overflow-hidden truncate`}
          dangerouslySetInnerHTML={{ __html: highlightText(userRole.right) }}
        />
      );

    const teamField =
      editTeamId === idKey ? (
        (() => {
          const selected = userRole.team
            ? teamOptions.find((opt) => String(opt.value) === String(userRole.team?.id)) ?? {
                value: String(userRole.team.id),
                label: userRole.team.name,
              }
            : teamOptions[0];

          return (
            <Select
              value={selected}
              isSearchable={false}
              options={teamOptions}
              onChange={(opt) => handleTeamChange(opt, idKey)}
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPlacement="auto"
              menuPosition="fixed"
              styles={commonSelectStyles as any}
            />
          );
        })()
      ) : (
        <span
          className="block min-w-0 max-w-full overflow-hidden truncate text-slate-600 dark:text-slate-300"
          dangerouslySetInnerHTML={{ __html: highlightText(userRole.team?.name ?? "-") }}
        />
      );

    const actions = rowCanEdit ? (
      <>
        {isEditing && (
          <button
            aria-label="Cancel"
            className="h-9 w-9 inline-flex items-center justify-center rounded-lg transition hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-200"
            onClick={() => cancelEdit(idKey)}
          >
            <AiOutlineClose size={16} />
          </button>
        )}

        <button
          aria-label="Edit"
          className={cn(
            "h-9 w-9 inline-flex items-center justify-center rounded-lg transition",
            rowCanManage
              ? "hover:bg-slate-100 dark:hover:bg-slate-800"
              : "opacity-50 cursor-not-allowed",
          )}
          onClick={() => {
            if (!rowCanManage) return;
            if (isEditing) saveEdit(idKey);
            else startEdit(idKey);
          }}
          disabled={!rowCanManage}
        >
          {isEditing ? (
            <BiSave size={16} className="text-slate-600 dark:text-slate-200" />
          ) : (
            <AiOutlineEdit size={16} className="text-slate-600 dark:text-slate-200" />
          )}
        </button>

        {!isEditing && (
          <DeleteButton
            disabled={!rowCanDelete}
            onClick={() => {
              if (!rowCanDelete) return;
              if (userRole?.id != null) {
                setShowRemoveUserRoleModal({ visible: true, id: userRole.id });
              }
            }}
          />
        )}
      </>
    ) : (
      <span className="text-slate-400 text-sm">—</span>
    );

    return (
      <div style={style}>
        <div
          ref={rowRef}
          className={[
            "grid items-stretch text-sm",
            "border-b border-slate-200 dark:border-slate-700",
            "divide-x divide-slate-200 dark:divide-slate-700",
            index % 2 === 0 ? "bg-white/50 dark:bg-slate-900/15" : "bg-transparent",
            "hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors",
          ].join(" ")}
          style={{
            ...TABLE_GRID_STYLE,
            boxSizing: "border-box",
            ...(isSelf
              ? { backgroundColor: darkMode ? "rgba(134,188,37,0.06)" : "rgba(134,188,37,0.06)" }
              : {}),
          }}
        >
          <div className="px-4 py-3 min-w-0 overflow-hidden flex items-center gap-3 h-full">
            <div className="h-9 w-9 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-semibold text-slate-700 dark:text-slate-200 shrink-0">
              {getInitials(userRole.user?.name)}
            </div>

            <div className="min-w-0 w-full">
              <div
                className="min-w-0 overflow-hidden truncate font-medium text-slate-600 dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: highlightText(userRole.user?.name || "-") }}
              />

              {showInlineMeta && (
                <div className="mt-1 min-w-0 overflow-hidden truncate text-xs text-slate-500 dark:text-slate-400">
                  {userRole.team?.name ?? "-"}
                </div>
              )}
            </div>
          </div>

          <div className="px-4 py-3 min-w-0 overflow-hidden h-full">
            {!showInlineMeta ? (
              <div
                className="truncate flex items-center h-full text-slate-600 dark:text-slate-300"
                dangerouslySetInnerHTML={{ __html: highlightText(userRole.user?.email || "-") }}
              />
            ) : (
              <div className="min-w-0 h-full flex flex-col justify-center">
                <div
                  className="min-w-0 overflow-hidden truncate text-slate-600 dark:text-slate-300"
                  dangerouslySetInnerHTML={{ __html: highlightText(userRole.user?.email || "-") }}
                />

                <div className="mt-2 flex flex-col gap-2 min-w-0">
                  <div className="min-w-0">{roleField}</div>
                  <div className="min-w-0">{teamField}</div>

                  {showActionsInline && (
                    <div className="flex items-center justify-end gap-2">{actions}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {hasRoleColumn && (
            <div className="px-4 py-3 min-w-0 overflow-hidden flex items-center h-full">
              {roleField}
            </div>
          )}

          {hasTeamColumn && (
            <div className="px-4 py-3 min-w-0 overflow-hidden flex items-center h-full">
              {teamField}
            </div>
          )}

          {hasActionsColumn && (
            <div className="px-4 py-3 flex items-center justify-center gap-2">{actions}</div>
          )}
        </div>
      </div>
    );
  };

  const showingText = useMemo(() => {
    const shown = filteredUsers.length;
    const total = userRoles.length;
    const fallback = `Showing ${shown} of ${total} users`;

    const translated = t("adminPanel.showingUsers", {
      shown,
      total,
      count: shown,
      filtered: shown,
      filteredCount: shown,
      totalCount: total,
      users: total,
      totalUsers: total,
      defaultValue: fallback,
    });

    if (
      typeof translated === "string" &&
      (translated.includes("...") || translated.includes("{{") || translated.includes("undefined"))
    ) {
      return fallback;
    }
    return translated;
  }, [t, filteredUsers.length, userRoles.length]);

  const btnBase =
    "inline-flex items-center justify-center h-10 px-4 rounded-lg text-sm font-medium transition";
  const btnGreen = `${btnBase} bg-del-green text-white hover:opacity-90`;
  const btnWhite =
    `${btnBase} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 ` +
    `text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800`;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
              {t("adminPanel.manageUsersTab", { defaultValue: "Manage Users" })}{" "}
              {selectedTeam ? `(${selectedTeam.name})` : ""}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {t("adminPanel.manageUsersSubtitle", {
                defaultValue: "Add, edit, and manage user permissions",
              })}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {canEdit && (
              <>
                <button className={btnGreen} onClick={() => setShowAddUserModal(true)}>
                  <FaPlus className="mr-2" />
                  {t("adminPanel.addUser")}
                </button>

                {showAddUserModal && (
                  <AddUserModal
                    setShowAddUserModal={setShowAddUserModal}
                    selectedTeam={selectedTeam}
                    handleAddUser={handleAddUser}
                    roleOptions={roleOptions}
                    teamListOptions={teamList?.map((team: ITeamList) => ({
                      value: team.id.toString(),
                      label: team.name,
                    }))}
                    darkMode={darkMode}
                    userList={userList}
                    user={user}
                  />
                )}
              </>
            )}

            {user?.is_superuser && (
              <>
                <button className={btnWhite} onClick={() => setShowAddSuperuserModal(true)}>
                  <FaPlus className="mr-2 text-del-green" />
                  {t("adminPanel.addSuperuser")}
                </button>

                {showAddSuperuserModal && (
                  <AddSuperuserModal
                    setShowAddSuperuserModal={setShowAddSuperuserModal}
                    handleAddSuperuser={handleAddSuperuser}
                    roleOptions={{ value: "0", label: "Superuser" }}
                    darkMode={darkMode}
                    userList={userList}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-start gap-4">
          <div className="flex-1 min-w-[260px] max-w-md">
            <SearchTableInput
              onInputChange={getSearchValue}
              placeholderText={t("adminPanel.searchInputPlaceholder", {
                defaultValue: "Search: name, email, role, team",
              })}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 bg-slate-100 dark:bg-slate-950/20 p-6 overflow-hidden">
        <div className="h-full min-h-0 flex flex-col">
          <div className="flex-1 min-h-0">
            <div className="h-full bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col">
              {!userRoles ? (
                <div className="text-center p-6">
                  <FaSpinner
                    className="animate-spin inline-block"
                    size={24}
                    color={darkMode ? "#FFFFFF" : "#000000"}
                  />
                </div>
              ) : (
                <>
                  <div ref={listAreaRef} className="flex-1 min-h-0 relative flex flex-col">
                    {filteredUsers.length === 0 ? (
                      <div className="h-full min-h-0 overflow-auto">
                        <div style={{ minWidth: tableMinWidth }}>
                          <HeaderRow />
                          <div className="p-6 flex items-center justify-center min-h-[220px]">
                            <p className="text-sm text-slate-600 dark:text-slate-300">
                              {t("noData")}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <VariableSizeList
                        ref={listRef as any}
                        outerRef={listOuterRef as any}
                        outerElementType={OuterElement as any}
                        innerElementType={InnerElement as any}
                        height={listHeight}
                        itemCount={itemCount}
                        itemSize={getRowSize as any}
                        width="100%"
                        itemKey={(index) => getItemKey(index)}
                        overscanCount={5}
                      >
                        {Row as any}
                      </VariableSizeList>
                    )}
                  </div>

                  <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                    {showingText}
                  </div>
                </>
              )}
            </div>
          </div>

          {showRemoveUserRoleModal.visible && (
            <ModalConfirmWindow
              setShowModal={setShowRemoveUserRoleModal as any}
              handleDeleteUser={handleRemoveUserRole}
              message={t("adminPanel.deleteUserTeam")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export { ManageUserTab };